import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClinic } from '@/contexts/ClinicContext';
import { patientsApi } from '@/services/api';
import { formatDate } from '@/utils/dateTime';
import { cn, formatPhone, formatPatientAge } from '@/lib/utils';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MoreHorizontal,
  Trash2,
  Edit,
  Eye,
  Users,
  Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AuthorizedButton } from '@/components/common/AuthorizedButton';
import { HasPermission } from '@/components/auth/HasPermission';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const bloodGroups = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const PatientListPage = () => {
  const navigate = useNavigate();
  const { buildUrl, hospitalId } = useClinic();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('all');
  const [bloodGroupFilter, setBloodGroupFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1); // Reset page on new search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, genderFilter, bloodGroupFilter, hospitalId]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const response = await patientsApi.getAll(
        {
          page,
          limit: 10,
          search: debouncedSearch || undefined,
          gender: genderFilter !== 'all' ? genderFilter : undefined,
          bloodGroup: bloodGroupFilter !== 'all' ? bloodGroupFilter : undefined,
        },
        hospitalId
      );
      setPatients(response.data);
      setTotalPages(response.totalPages);
      setTotal(response.total);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchInput(e.target.value);
  };

  const handleDelete = async () => {
    if (!patientToDelete) return;
    try {
      await patientsApi.delete(patientToDelete.id);
      toast.success('Patient deleted successfully');
      loadPatients();
    } catch (error) {
      toast.error('Failed to delete patient');
    } finally {
      setDeleteDialogOpen(false);
      setPatientToDelete(null);
    }
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="space-y-6" data-testid="patient-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Patients</h1>
          <p className="text-slate-500">Manage patient records and information</p>
        </div>
        <AuthorizedButton
          permission="PATIENT_WRITE"
          tooltipMessage="You don't have access to register new patients"
          onClick={() => navigate(buildUrl('patients/new'))}
          className="bg-teal-600 hover:bg-teal-700"
          data-testid="add-patient-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Patient
        </AuthorizedButton>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="search"
                placeholder="Search by name, phone, Aadhaar, or ID..."
                value={searchInput}
                onChange={handleSearch}
                className="pl-10"
                data-testid="patient-search"
              />
            </div>
            <div className="flex gap-2">
              <Select value={genderFilter} onValueChange={setGenderFilter}>
                <SelectTrigger className="w-32" data-testid="gender-filter">
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Select value={bloodGroupFilter} onValueChange={setBloodGroupFilter}>
                <SelectTrigger className="w-32" data-testid="blood-group-filter">
                  <SelectValue placeholder="Blood Group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Blood Groups</SelectItem>
                  {bloodGroups.map(bg => (
                    <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card>
        <CardHeader className="pb-0">
          <CardTitle className="text-base font-medium">
            {total} Patient{total !== 1 ? 's' : ''} Found
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : patients.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Age / Gender</TableHead>
                      <TableHead>Blood Group</TableHead>
                      <TableHead>Last Visit</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patients.map((patient) => (
                      <TableRow 
                        key={patient.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(buildUrl(`patients/${patient.id}`))}
                        data-testid={`patient-row-${patient.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10">
                              <AvatarFallback className="bg-teal-100 text-teal-700">
                                {patient.firstName[0]}{patient.lastName[0]}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium text-slate-900">
                                {patient.firstName} {patient.lastName}
                              </p>
                              <p className="text-xs text-slate-500 font-mono">
                                {patient.patientCode || (patient.id ? `ID: ${patient.id.slice(0, 8)}...` : '')}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-900 font-mono">{formatPhone(patient.phone)}</p>
                          <p className="text-xs text-slate-500">{patient.email || '-'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-900">
                            {formatPatientAge(patient.age, patient.dateOfBirth)}
                          </p>
                          <p className="text-xs text-slate-500 capitalize">{patient.gender || '-'}</p>
                        </TableCell>
                        <TableCell>
                          {patient.bloodGroup ? (
                            <Badge variant="outline" className="font-mono">
                              {patient.bloodGroup}
                            </Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-600">
                            {patient.lastVisit ? formatDate(patient.lastVisit, 'DEFAULT') : '-'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-xs font-semibold uppercase tracking-wider',
                              (patient.status || 'ACTIVE').toUpperCase() === 'ACTIVE'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : (patient.status || '').toUpperCase() === 'INACTIVE'
                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                : 'bg-slate-100 text-slate-700 border-slate-300'
                            )}
                          >
                            {patient.status || 'ACTIVE'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`action-menu-${patient.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(buildUrl(`patients/${patient.id}`))}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              <HasPermission permission="PATIENT_WRITE">
                                <DropdownMenuItem onClick={() => navigate(buildUrl(`patients/${patient.id}/edit`))}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit Patient
                                </DropdownMenuItem>
                              </HasPermission>
                              <HasPermission permission="APPOINTMENT_WRITE">
                                <DropdownMenuItem onClick={() => navigate(buildUrl(`appointments/new?patientId=${patient.id}`))}>
                                  <Calendar className="mr-2 h-4 w-4" />
                                  Book Appointment
                                </DropdownMenuItem>
                              </HasPermission>
                              <HasPermission permission="PATIENT_DELETE">
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    setPatientToDelete(patient);
                                    setDeleteDialogOpen(true);
                                  }}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </HasPermission>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    data-testid="next-page-btn"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12" data-testid="empty-patients">
              <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">No patients found</h3>
              <p className="text-slate-500 mb-4">
                {debouncedSearch ? 'Try adjusting your search terms' : 'Get started by adding your first patient'}
              </p>
              {!debouncedSearch && (
                <AuthorizedButton
                  allowedRoles={['admin', 'receptionist', 'doctor', 'nurse', 'staff']}
                  tooltipMessage="You don't have access to register new patients"
                  onClick={() => navigate(buildUrl('patients/new'))}
                  data-testid="empty-add-patient"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Patient
                </AuthorizedButton>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Patient</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {patientToDelete?.firstName} {patientToDelete?.lastName}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PatientListPage;
